// ── 3-3. Divergence: where the two spaces disagree ─────────────────
//
// THE SIGNATURE IS THE POINT: divergence(reading, snapshot).
//
// A reading is not enough to compute a divergence and never will be. The
// measurement is a comparison of two spaces, and the second one — the
// arrangement the user actually made — is not recoverable from the first: the
// reading was built without it, on purpose. Anything shaped like
// `divergence(reading)` or `reading.divergence` is asking the model's output to
// account for the user's, which is the exact confusion this instrument exists
// to take apart.
//
// It is also not one-to-one. 2-4 keys the cache on content, so the same stored
// reading can be asked for from two different arrangements: one reading, two
// snapshots, two divergences. So the pair is the unit of measurement, the
// snapshot is named in what comes back, and the UI shows which one a number
// came from. A divergence figure with no arrangement attached is unreadable —
// the user cannot tell which of their own layouts it is about.

// Extension included so this module resolves under plain Node as well as under
// webpack — scripts/verify-3-3-signature.mjs imports the shipped file itself,
// not a copy, and a copy is not evidence about what ships.
import { frozenGeometry } from "./freeze.js";

// Both arguments are required, and this throws rather than returning something
// empty. A silent null here would surface as "no divergence found", which reads
// as agreement between the two spaces — a measurement claim, from a bug.
export function divergence(reading, snapshot) {
  if (!reading) throw new TypeError("divergence(reading, snapshot): no reading");
  if (!snapshot) throw new TypeError(
    "divergence(reading, snapshot): no arrangement. A reading cannot be compared to itself — " +
    "pass the snapshot it was frozen against."
  );

  const geo = frozenGeometry(snapshot);
  const placed = new Set(geo.points.map(p => p.id));
  const spokenOf = new Set([
    ...(reading.relations || []).flatMap(r => [r.a, r.b]),
    ...(reading.unlinked || []),
    ...(reading.fragments || []).map(f => f.id),
  ]);

  return {
    // Which arrangement this is about. Carried in the result so a number can
    // never be displayed without the layout it was measured against.
    snapshotId: snapshot.id,
    frozenAt: snapshot.frozenAt,
    field: snapshot.field,

    // Integrity, not measurement. A cached reading paired with a later
    // arrangement can legitimately mention a fragment that has since been
    // deleted, or miss one added after. 3-3's metrics have to be computed over
    // the intersection, and the leftovers have to be shown rather than dropped
    // silently — the same rule as the "1 of 3" counts.
    unpaired: {
      readingOnly: [...spokenOf].filter(id => !placed.has(id)).sort(),
      arrangementOnly: [...placed].filter(id => !spokenOf.has(id)).sort(),
    },

    // Both measured over the INTERSECTION — a relation naming a fragment that
    // is not in the arrangement cannot be ranked in it. Those are counted in
    // `unpaired` above rather than dropped.
    modelSurprise: modelSurprise(reading, geo, placed),
    unreadAdjacency: unreadAdjacency(reading, geo, placed),
  };
}

// ── the two measurements ───────────────────────────────────────────
// Both are RANK-based for display (spec §0.3: no claim from raw coordinate
// movement), and both carry their raw distances with them. That is not
// redundancy — it is the only thing verification can stand on. Rank collapses
// harder than the five resonance bands do: it keeps order and throws away
// everything else, so a distance calculation that is wrong in every value
// still passes a rank check whenever the ordering survives. The numbers below
// travel with the ranks so a test can compare them to hand arithmetic.

// Neighbours of one point, nearest first. Ties break on id so the ordering is
// total and the same on every machine.
function neighboursOf(id, geo) {
  const self = geo.points.find(p => p.id === id);
  return geo.points
    .filter(p => p.id !== id)
    .map(p => ({ id: p.id, distance: geo.distance(self, p) }))
    .sort((m, n) => (m.distance - n.distance) || (m.id < n.id ? -1 : m.id > n.id ? 1 : 0));
}

// For every relation the model drew: how near were those two in the
// arrangement the user made? "4th-nearest out of 5" is the surprise.
function modelSurprise(reading, geo, placed) {
  const of = Math.max(0, geo.points.length - 1);
  const ranks = new Map(geo.points.map(p => [p.id, neighboursOf(p.id, geo)]));
  const rankIn = (from, to) => ranks.get(from).findIndex(nb => nb.id === to) + 1;

  const relations = (reading.relations || [])
    .filter(r => placed.has(r.a) && placed.has(r.b) && r.a !== r.b)
    .map(r => {
      // A relation has no direction but a rank does: b can be a's 4th-nearest
      // while a is b's 1st, when b sits in a crowd and a sits alone. Reporting
      // one direction would hide that, so both are kept and the HEADLINE rank
      // is the smaller one. Surprise is a claim about the user's arrangement,
      // and the conservative reading is to call it surprising only when even
      // the kinder direction says the two were not near each other.
      const rankFromA = rankIn(r.a, r.b);
      const rankFromB = rankIn(r.b, r.a);
      return {
        a: r.a, b: r.b, fit: r.fit, label: r.label,
        rank: Math.min(rankFromA, rankFromB),
        rankFromA, rankFromB, of,
        distance: geo.distance(
          geo.points.find(p => p.id === r.a),
          geo.points.find(p => p.id === r.b)
        ),
      };
    })
    .sort((m, n) => (n.rank - m.rank) || (m.a + m.b < n.a + n.b ? -1 : 1));

  return {
    relations,
    of,
    total: relations.length,
    // Relations that could not be ranked because an endpoint is not on this
    // arrangement. Stated, never folded into the total.
    unrankable: (reading.relations || []).filter(r => !placed.has(r.a) || !placed.has(r.b)).length,
  };
}

// The other direction: pairs the user placed closest, that the model did not
// link strongly.
//
// "Closest" is each fragment's OWN nearest neighbour, not a top-N slice of all
// pairs. A cutoff would be an invented number, and the doc forbids a silent
// one; this set is defined by the arrangement itself — for each fragment, the
// one thing the user put nearest to it — so its size is a fact about the
// canvas rather than a display choice.
function unreadAdjacency(reading, geo, placed) {
  const strong = new Set(), any = new Map();
  for (const r of reading.relations || []) {
    const key = [r.a, r.b].sort().join(" ");
    if (r.fit === "strong") strong.add(key);
    if (!any.has(key)) any.set(key, r.fit);
  }

  const seen = new Set(), pairs = [];
  for (const p of geo.points) {
    const nearest = neighboursOf(p.id, geo)[0];
    if (!nearest) continue;
    const key = [p.id, nearest.id].sort().join(" ");
    if (seen.has(key)) continue;
    seen.add(key);
    if (strong.has(key)) continue;          // the model did link it strongly
    const [a, b] = key.split(" ");
    pairs.push({
      a, b,
      distance: nearest.distance,
      // "not linked at all" and "linked, but only weakly" are different
      // readings and must not be merged into one absence.
      modelFit: any.has(key) ? any.get(key) : null,
    });
  }
  pairs.sort((m, n) => (m.distance - n.distance) || (m.a < n.a ? -1 : 1));

  return {
    pairs,
    total: pairs.length,
    // How many nearest-neighbour pairs there were in all, so "3 of 5" can be
    // said rather than a list of 3 implying there were only 3.
    ofNearestPairs: seen.size,
    placed: placed.size,
  };
}

// How the UI names an arrangement. Short id so two are visibly different, and
// the time so the user can place it in their own session.
//
// The seed is the exception, and it has to be. Its arrangement really is laid
// out in this session — the id is honest — but its reading is precomputed, so
// stamping the moment reads as "this reading happened at 00:11" to someone
// seeing the instrument for the first time. `frozenAt` names a moment and the
// seed's reading has none, so a prepared snapshot says what it is instead.
//
// One flag, read here, so the panel and the canvas cannot describe the same
// snapshot differently — they both call this.
export function snapshotLabel(snapshot) {
  if (!snapshot) return null;
  const named = `arrangement ${snapshot.id.slice(0, 6)}`;
  if (snapshot.prepared) return `${named} · prepared example`;
  const t = new Date(snapshot.frozenAt);
  const hh = String(t.getHours()).padStart(2, "0");
  const mm = String(t.getMinutes()).padStart(2, "0");
  return `${named} · frozen ${hh}:${mm}`;
}
